import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { TrashIcon, PencilIcon, ChatBubbleLeftIcon, UserGroupIcon, CalendarIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import AddStepButton from '../steps/AddStepButton';
import RaciAssignments from '../assignments/RaciAssignments';
import VideoPlayer from '../clientPortal/VideoPlayer';

// VideoModal component
const VideoModal = ({ isOpen, onClose, videoUrl, title }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>
        
        <div className="relative bg-white rounded-lg max-w-4xl w-full mx-auto shadow-xl overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium">{title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="w-full" style={{ height: "480px" }}>
            <VideoPlayer url={videoUrl} />
          </div>
          
          <div className="p-4 bg-gray-50 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              This video provides instructions and information about this phase.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const PhaseDetails = () => {
  const [phase, setPhase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availableStepStatuses, setAvailableStepStatuses] = useState([]);
  const [activeTab, setActiveTab] = useState('steps'); // 'steps' or 'assignments'
  
  // Video modal state
  const [showVideoModal, setShowVideoModal] = useState(false);
  
  // Form state
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const [editVideoUrl, setEditVideoUrl] = useState('');
  
  const { id } = useParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchPhase = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await api.get(`/phases/${id}`);
        setPhase(response.data.phase);
        
        // Set form state for editing
        setEditName(response.data.phase.name || '');
        setEditDescription(response.data.phase.description || '');
        setEditStatus(response.data.phase.status || '');
        // Format due date for date input
        setEditDueDate(response.data.phase.due_date ? new Date(response.data.phase.due_date).toISOString().split('T')[0] : '');
        setEditVideoUrl(response.data.phase.video_url || '');
      } catch (err) {
        console.error('Error fetching phase details:', err);
        setError('Failed to load phase data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPhase();
    
    // Fetch available status types for phases
    const fetchStatusTypes = async () => {
      try {
        const response = await api.get('/settings/status-types/phase');
        if (response.data && response.data.statusTypes) {
          setAvailableStatuses(response.data.statusTypes);
        }
        
        // Also fetch step status types for displaying step statuses
        const stepStatusResponse = await api.get('/settings/status-types/step');
        if (stepStatusResponse.data && stepStatusResponse.data.statusTypes) {
          setAvailableStepStatuses(stepStatusResponse.data.statusTypes);
        }
      } catch (error) {
        console.error('Error fetching status types:', error);
      }
    };
    
    fetchStatusTypes();
  }, [id]);
  
  const handleStepAdded = () => {
    // Refresh phase data to include the new step
    const fetchPhase = async () => {
      try {
        const response = await api.get(`/phases/${id}`);
        setPhase(response.data.phase);
      } catch (err) {
        console.error('Error refreshing phase details:', err);
      }
    };
    
    fetchPhase();
  };
  
  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await api.delete(`/phases/${id}`);
      setShowDeleteModal(false);
      navigate(`/projects/${phase.project_id}`);
    } catch (err) {
      console.error('Error deleting phase:', err);
      alert('Failed to delete phase. Please try again.');
      setIsDeleting(false);
    }
  };
  
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editName.trim()) {
      alert('Phase name is required');
      return;
    }
    
    try {
      setIsEditing(true);
      
      const response = await api.put(`/phases/${id}`, {
        name: editName,
        description: editDescription,
        status: editStatus,
        due_date: editDueDate || null,
        video_url: editVideoUrl.trim() || null
      });
      
      setPhase(response.data.phase);
      setShowEditModal(false);
    } catch (err) {
      console.error('Error updating phase:', err);
      alert('Failed to update phase. Please try again.');
    } finally {
      setIsEditing(false);
    }
  };
  
  // Open video modal
  const handleOpenVideo = () => {
    if (phase && phase.video_url) {
      setShowVideoModal(true);
    }
  };
  
  // Format date for display
  const formatDueDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="w-12 h-12 border-t-2 border-b-2 border-gray-500 rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600">Loading phase details...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-4 text-red-700 bg-red-100 rounded-md">
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="mt-2 text-red-700 underline"
        >
          Retry
        </button>
      </div>
    );
  }
  
  if (!phase) {
    return <div>Phase not found</div>;
  }
  
  return (
    <div>
      <div className="mb-5 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{phase.name}</h1>
          <p className="text-sm font-medium text-gray-500">
            <Link 
              to={`/projects/${phase.project_id}`} 
              className="text-blue-600 hover:text-blue-800"
            >
              Back to Project
            </Link>
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowEditModal(true)}
            className="p-1 rounded-full text-blue-600 hover:bg-blue-100 focus:outline-none"
            aria-label="Edit phase"
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="p-1 rounded-full text-red-600 hover:bg-red-100 focus:outline-none"
            aria-label="Delete phase"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
      
      {phase.description && (
        <div className="bg-gray-50 rounded-md p-4 mb-6">
          <h3 className="text-sm font-medium text-gray-500 mb-1">Description</h3>
          <p className="text-gray-900">{phase.description}</p>
        </div>
      )}
      
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:mt-2 sm:space-x-6">
        {phase.status && (
          <div className="mt-2 flex items-center text-sm">
            <span className="mr-1.5 font-medium">Status:</span>
            <span 
              className="px-2 py-1 rounded-full text-xs font-medium inline-flex items-center justify-center"
              style={{
                backgroundColor: availableStatuses.find(s => s.name === phase.status)?.color || '#6B7280',
                color: 'white',
                minWidth: '80px',
                textAlign: 'center'
              }}
            >
              {phase.status}
            </span>
          </div>
        )}
        
        {/* Display Due Date */}
        <div className="mt-2 flex items-center text-sm">
          <span className="mr-1.5 font-medium">Due Date:</span>
          <span className="flex items-center">
            <CalendarIcon className="h-4 w-4 mr-1 text-gray-500" />
            {formatDueDate(phase.due_date)}
          </span>
        </div>
        
        {/* Display Video button if video exists */}
        {phase.video_url && (
          <div className="mt-2 flex items-center text-sm">
            <button
              onClick={handleOpenVideo}
              className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <VideoCameraIcon className="h-4 w-4 mr-2" />
              Watch Tutorial Video
            </button>
          </div>
        )}
      </div>
      
      {/* Tabs */}
      <div className="mt-6 border-b border-gray-200">
        <nav className="-mb-px flex">
          <button
            onClick={() => setActiveTab('steps')}
            className={`${
              activeTab === 'steps'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm mr-8`}
          >
            Steps
          </button>
          <button
            onClick={() => setActiveTab('assignments')}
            className={`${
              activeTab === 'assignments'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <UserGroupIcon className="h-5 w-5 mr-1" />
            Assignments
          </button>
        </nav>
      </div>
      
      {/* Tab Content */}
      {activeTab === 'steps' ? (
        <div className="mb-6 mt-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Steps</h2>
            <AddStepButton phaseId={phase.id} onStepAdded={handleStepAdded} />
          </div>
          
          {phase.steps && phase.steps.length > 0 ? (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {phase.steps.map((step) => (
                  <li key={step.id} className="px-4 py-4 hover:bg-gray-50">
                    <Link to={`/steps/${step.id}`} className="block">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">{step.name}</h3>
                          {step.description && (
                            <p className="mt-1 text-sm text-gray-500">{step.description}</p>
                          )}
                          {/* Display step due date if available */}
                          {step.due_date && (
                            <p className="mt-1 text-xs text-gray-500 flex items-center">
                              <CalendarIcon className="h-3 w-3 mr-1" />
                              Due: {formatDueDate(step.due_date)}
                            </p>
                          )}
                          {/* Display video icon if step has a video */}
                          {step.video_url && (
                            <p className="mt-1 text-xs text-blue-500 flex items-center">
                              <VideoCameraIcon className="h-3 w-3 mr-1" />
                              Video tutorial available
                            </p>
                          )}
                        </div>
                        <span 
                          className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full items-center justify-center"
                          style={{
                            backgroundColor: availableStepStatuses.find(s => s.name === step.status)?.color || '#6B7280',
                            color: 'white',
                            minWidth: '80px',
                            textAlign: 'center'
                          }}
                        >
                          {step.status || 'Not Started'}
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="p-4 bg-gray-50 rounded-md text-center text-gray-500">
              No steps added yet. Use the "Add Step" button to add your first step.
            </div>
          )}
        </div>
      ) : activeTab === 'assignments' ? (
        <div className="mt-6">
          <RaciAssignments itemType="phase" itemId={id} />
        </div>
      ) : null}
      
      {/* Video Modal */}
      {showVideoModal && phase.video_url && (
        <VideoModal
          isOpen={showVideoModal}
          onClose={() => setShowVideoModal(false)}
          videoUrl={phase.video_url}
          title={`Video Tutorial: ${phase.name}`}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete Phase
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete this phase? All steps in this phase will be permanently removed. This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className={`inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleEditSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                      <PencilIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Edit Phase
                      </h3>
                      <div className="mt-2">
                        <div className="mb-4">
                          <label htmlFor="editName" className="block text-sm font-medium text-gray-700">
                            Phase Name *
                          </label>
                          <input
                            type="text"
                            name="editName"
                            id="editName"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            placeholder="Enter phase name"
                            required
                          />
                        </div>
                        <div className="mb-4">
                          <label htmlFor="editDescription" className="block text-sm font-medium text-gray-700">
                            Description
                          </label>
                          <textarea
                            id="editDescription"
                            name="editDescription"
                            rows={3}
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            placeholder="Enter phase description"
                          />
                        </div>
                        <div className="mb-4">
                          <label htmlFor="editVideoUrl" className="block text-sm font-medium text-gray-700">
                            Video URL
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <VideoCameraIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                            </div>
                            <input
                              type="url"
                              name="editVideoUrl"
                              id="editVideoUrl"
                              value={editVideoUrl}
                              onChange={(e) => setEditVideoUrl(e.target.value)}
                              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                              placeholder="https://example.com/video"
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            Enter a URL to a video tutorial for this phase, or leave blank to remove
                          </p>
                        </div>
                        <div className="mb-4">
                          <label htmlFor="editStatus" className="block text-sm font-medium text-gray-700">
                            Status
                          </label>
                          <select
                            id="editStatus"
                            name="editStatus"
                            value={editStatus}
                            onChange={(e) => setEditStatus(e.target.value)}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          >
                            <option value="">Select status</option>
                            {availableStatuses.map((status) => (
                              <option key={status.id} value={status.name}>
                                {status.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="editDueDate" className="block text-sm font-medium text-gray-700">
                            Due Date
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <CalendarIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                            </div>
                            <input
                              type="date"
                              name="editDueDate"
                              id="editDueDate"
                              value={editDueDate}
                              onChange={(e) => setEditDueDate(e.target.value)}
                              className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className={`inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={isEditing}
                  >
                    {isEditing ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => setShowEditModal(false)}
                    disabled={isEditing}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhaseDetails; 